import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import {
  getBarColors,
  SvgElement,
  useUniqueSelector,
  VisualizationUpdater,
} from '@mentimeter/question-modules-shared';
import type { DotsNode, DotsResultT } from '@mentimeter/visualizations';
import { Dots as DotsVisualization } from '@mentimeter/visualizations';
import * as React from 'react';
import { usePrevious } from '@mentimeter/react-hooks';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import type { SegmentT } from '@mentimeter/http-clients';
import { useRagnar } from '@mentimeter/ragnar-react';
import {
  SEGMENTED_LEGEND_LAYOUTS,
  SEGMENTED_BUCKET_LAYOUTS,
  BUCKET_LAYOUTS,
} from './utils/dotsHelper';
import SegmentationLegend from './components/SegmentationLegend';
import type { ChoicesVisualizationProps } from './types';

interface DotsT {
  selector: string;
  enableAnimations: boolean;
  colors: string[];
  backgroundColor: string;
  results: DotsResultT[];
  respondents: number;
  bucketsLayout: {
    [key: string]: { x: number; y: number }[];
  };
  numColumns: number;
  numBuckets: number;
  dotRadius: number;
  segments: SegmentT[];
  showCorrect: boolean;
  showResults: boolean;
  isSegmented: boolean;
  segmentedQuestionTitle: string;
  showPercent: boolean;
  textColor: string;
  segmentedLegendLineColor: string;
  correctColor: string;
  incorrectColor: string;
  chartWidth: number;
  chartHeight: number;
}

const DOTS_TO_BUCKETS_DELAY = 500;
const DOT_LARGE_RADIUS = 20;
const DOT_MEDIUM_RADIUS = 12;
const DOT_SMALL_RADIUS = 8;
// Ratio to determine when the dot radius should change
const DOTS_TO_CHOICES_RATIO_1 = 10;
const DOTS_TO_CHOICES_RATIO_2 = 60;

// Max number of dots to be rendered at once
const MAX_DOTS = 1000;

const getUniqueId = () =>
  new Date().getTime().toString(36).slice(-3) +
  Math.random().toString(36).slice(-10);

const C = React.memo<DotsT>(
  ({
    selector,
    colors,
    textColor,
    backgroundColor,
    results,
    respondents,
    bucketsLayout,
    numColumns,
    numBuckets,
    dotRadius,
    segments,
    showCorrect,
    showResults,
    isSegmented,
    showPercent,
    segmentedQuestionTitle,
    segmentedLegendLineColor,
    enableAnimations,
    correctColor,
    incorrectColor,
    chartWidth,
    chartHeight,
  }: DotsT) => {
    const [nodes, setNodes] = React.useState<DotsNode[]>([]);
    const [isPercentageData, setIsPercentageData] = React.useState(showPercent);
    const bucketWidth = React.useMemo(() => {
      const bucketsAreaWidth = (chartWidth * (numColumns - 1)) / numColumns;
      return isSegmented
        ? bucketsAreaWidth / numColumns
        : chartWidth / numColumns;
    }, [chartWidth, isSegmented, numColumns]);
    const segmentedLegendWidth = React.useMemo(
      () => chartWidth / numColumns,
      [chartWidth, numColumns],
    );
    const segmentationLegendPosition = React.useMemo(() => {
      if (results.length > 0) {
        const legendLayout = SEGMENTED_LEGEND_LAYOUTS[results.length - 1];
        return legendLayout ? legendLayout.x * chartWidth : 0;
      }
      return 0;
    }, [results.length, chartWidth]);
    // Prev props
    const prevRespondents = usePrevious(respondents, 0);
    const prevShowResults = usePrevious(showResults, true);
    const prevResults = usePrevious(results, []);
    const prevChartWidth = usePrevious(chartWidth, 0);

    const xPos = React.useCallback(
      (bucketIndex: number) => {
        const layout = bucketsLayout[numBuckets];
        if (layout && layout[bucketIndex]) {
          return chartWidth * layout[bucketIndex].x;
        }
        return 0;
      },
      [chartWidth, bucketsLayout, numBuckets],
    );
    const yPos = React.useCallback(
      (bucketIndex: number) => {
        const layout = bucketsLayout[numBuckets];
        if (layout && layout[bucketIndex]) {
          return chartHeight * layout[bucketIndex].y;
        }
        return 0;
      },
      [chartHeight, bucketsLayout, numBuckets],
    );

    const dots = React.useMemo(() => {
      return new DotsVisualization({
        bucketsLayout,
        xPos,
        yPos,
        incorrectColor,
        correctColor,
        isScoreHidden: !showResults,
      });
    }, [showResults, bucketsLayout, correctColor, incorrectColor, xPos, yPos]);

    React.useEffect(() => {
      dots.setSelector(`#${selector}`);
    }, [dots, selector]);

    React.useEffect(() => {
      dots.setDotRadius(dotRadius);
      dots.updateCollisions();
      dots.updateDots();
    }, [dots, dotRadius]);

    React.useEffect(() => {
      dots.setShowCorrect(showCorrect);
      dots.updateBuckets();
      dots.updateDots();
    }, [dots, showCorrect]);

    React.useEffect(() => {
      setIsPercentageData(showPercent);
    }, [showPercent]);

    React.useEffect(() => {
      dots.setShowPercent(isPercentageData);
      dots.updateBuckets();
    }, [dots, isPercentageData]);

    React.useEffect(() => {
      dots.setChartWidth(chartWidth);
    }, [dots, chartWidth]);

    React.useEffect(() => {
      dots.setChartHeight(chartHeight);
    }, [dots, chartHeight]);

    React.useEffect(() => {
      dots.setBucketWidth(bucketWidth);
    }, [dots, bucketWidth]);

    React.useEffect(() => {
      dots.setNumBuckets(numBuckets);
    }, [dots, numBuckets]);

    React.useEffect(() => {
      dots.setEnableAnimations(enableAnimations);
    }, [dots, enableAnimations]);

    React.useEffect(() => {
      dots.setBackgroundColor(backgroundColor);
    }, [dots, backgroundColor]);

    React.useEffect(() => {
      dots.setTextColor(textColor);
    }, [dots, textColor]);

    React.useEffect(() => {
      dots.setNodes(nodes);
      dots.updateForceNodes();
      dots.updateDots();
    }, [nodes, dots]);

    React.useEffect(() => {
      dots.setResults(results);
      dots.updateBuckets();
    }, [results, dots]);

    React.useEffect(() => {
      dots.setColors(colors);
      dots.updateDots();
    }, [dots, colors]);

    // Update nodes in force simulation
    React.useEffect(() => {
      const timer = setTimeout(
        () => {
          // Allow all nodes (dots) to move after maximum delay
          nodes.forEach((n) => (n.move = true));
          dots.moveDotsToBuckets();
        },
        enableAnimations ? DOTS_TO_BUCKETS_DELAY : 0,
      );
      return () => clearTimeout(timer);
    }, [nodes, dots, enableAnimations]);

    const numberOfColors = colors.length;

    // Reusable add nodes for current session and trends session
    const getAddedNodes = React.useCallback(
      (res: DotsResultT[], prevRes: DotsResultT[]) => {
        const newNodes: DotsNode[] = [];
        res.forEach((r, i) => {
          r.score.forEach((s, j) => {
            const prevScore =
              prevRes && prevRes[i] && prevRes[i].score && prevRes[i].score[j]
                ? prevRes[i].score[j]
                : 0;
            const dotsToAdd = s >= prevScore ? s - prevScore : s;
            for (let k = 0; k < dotsToAdd; k++) {
              newNodes.push({
                ...r,
                uniqueId: getUniqueId(),
                opacity: 1,
                colorIndex: isSegmented
                  ? j % numberOfColors
                  : i % numberOfColors,
                bucketIndex: i,
                x: enableAnimations ? chartWidth : xPos(i),
                y: enableAnimations ? chartHeight : yPos(i),
                move: false,
              });
            }
          });
        });
        return newNodes;
      },
      [
        chartWidth,
        chartHeight,
        isSegmented,
        numberOfColors,
        xPos,
        yPos,
        enableAnimations,
      ],
    );

    // Add new nodes
    const addNodes = React.useCallback(() => {
      const newNodes = getAddedNodes(results, prevResults);
      setNodes([...nodes, ...newNodes]);
    }, [nodes, prevResults, results, getAddedNodes]);

    // Reusable reset nodes for current session and trends session
    const getResetNodes = React.useCallback(
      (res: DotsResultT[]) => {
        const newNodes: DotsNode[] = [];
        res.forEach((r, i) => {
          r.score.forEach((s, j) => {
            const dotsToAdd = s;
            for (let k = 0; k < dotsToAdd; k++) {
              newNodes.push({
                ...r,
                uniqueId: getUniqueId(),
                opacity: 1,
                colorIndex: isSegmented
                  ? j % numberOfColors
                  : i % numberOfColors,
                bucketIndex: i,
                x: enableAnimations ? chartWidth : xPos(i),
                y: enableAnimations ? chartHeight : yPos(i),
                move: false,
              });
            }
          });
        });
        return newNodes;
      },
      [
        numberOfColors,
        chartWidth,
        chartHeight,
        isSegmented,
        enableAnimations,
        xPos,
        yPos,
      ],
    );

    // Reset nodes after clearing, hiding, or setting test results
    const resetNodes = React.useCallback(() => {
      const newNodes = getResetNodes(results);
      setNodes([...newNodes]);
    }, [results, getResetNodes]);

    // Add dots
    React.useEffect(() => {
      if (
        typeof prevRespondents === 'number' &&
        respondents > prevRespondents &&
        chartWidth > 0 &&
        respondents < MAX_DOTS
      ) {
        addNodes();
      }
    }, [respondents, prevRespondents, chartWidth, addNodes]);

    // Initialize nodes if tried to add dots before chart computed width available
    // or if changed to/from trends "benchmark" mode
    React.useEffect(() => {
      if (prevChartWidth === 0 && chartWidth > 0) {
        resetNodes();
      }
    }, [chartWidth, prevChartWidth, resetNodes]);

    // Clear dots
    React.useEffect(() => {
      // Need a check on total score as hiding results preserves # respondents
      if (
        prevShowResults !== showResults ||
        (typeof prevRespondents === 'number' && prevRespondents > respondents)
      ) {
        setNodes([]);
        resetNodes();
      }
    }, [
      respondents,
      prevRespondents,
      prevShowResults,
      showResults,
      resetNodes,
    ]);

    /* Position of dots, buckets and segmentation legend rely on the computed width of the container */
    if (chartWidth < 0) {
      return null;
    }
    return (
      <>
        <VisualizationUpdater updateFn={dots.update} />
        <SvgElement
          aria-hidden
          preserveAspectRatio="none"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          handleClick={() => setIsPercentageData((prev) => !prev)}
        >
          <g id={selector} data-testid="visualization-dots">
            {isSegmented && (
              <SegmentationLegend
                selector={`legend-${selector}`}
                title={segmentedQuestionTitle}
                segments={segments}
                width={segmentedLegendWidth}
                chartWidth={segmentationLegendPosition}
                chartHeight={chartHeight}
                colors={colors}
                textColor={textColor}
                lineColor={segmentedLegendLineColor}
              />
            )}
          </g>
        </SvgElement>
      </>
    );
  },
);

const Dots = ({
  results,
  showCorrect,
  shouldShowPercent,
  shouldHideResults,
  choices,
  shouldAnimate,
}: Pick<
  ChoicesVisualizationProps,
  | 'results'
  | 'showCorrect'
  | 'shouldShowPercent'
  | 'shouldHideResults'
  | 'choices'
  | 'shouldAnimate'
>) => {
  const { results: currentResults = [], respondents = 0 } = results;
  const { useSegmentation } = usePresentationContext();
  const {
    isSegmented,
    segmentedQuestionTitle = '',
    segments,
  } = useSegmentation();
  const {
    theme: {
      colors: {
        border: shade2,
        positive: correctColor,
        negative: incorrectColor,
      },
      visualizationColors: { fillColors, textColor, backgroundColor },
    },
  } = useRagnar();

  // Columns for grid layout settings
  const numColumns = React.useMemo(() => {
    switch (currentResults.length) {
      case 7:
      case 8:
      case 10:
      case 11:
      case 12:
        return 4;
      // There are not actually 3 columns by default, but we set the width
      // and position of buckets and the segmentation legend using a 3 or
      // 4 column grid
      default:
        return 3;
    }
  }, [currentResults]);
  const numBuckets = currentResults.length;

  const dotRadius = React.useMemo(() => {
    let dotRadius = DOT_LARGE_RADIUS;

    const dotsToChoicesRatio = respondents / currentResults.length;
    if (dotsToChoicesRatio > DOTS_TO_CHOICES_RATIO_1) {
      dotRadius = DOT_MEDIUM_RADIUS;
    }
    if (dotsToChoicesRatio > DOTS_TO_CHOICES_RATIO_2) {
      dotRadius = DOT_SMALL_RADIUS;
    }
    return dotRadius;
  }, [respondents, currentResults]);

  const data = currentResults.map((r, i) => ({
    ...r,
    correct_answer: choices[i]?.isMarkedCorrect || false,
  }));
  const bucketsLayout = isSegmented ? SEGMENTED_BUCKET_LAYOUTS : BUCKET_LAYOUTS;
  const colors = React.useMemo(
    () => getBarColors(fillColors, backgroundColor),
    [fillColors, backgroundColor],
  );
  const selector = useUniqueSelector();

  return (
    <FullSizeContainer>
      {(width, height) => (
        <C
          selector={selector}
          colors={colors}
          textColor={textColor}
          backgroundColor={backgroundColor}
          segmentedLegendLineColor={shade2}
          enableAnimations={shouldAnimate}
          respondents={respondents}
          results={data}
          showCorrect={showCorrect}
          showResults={!shouldHideResults}
          isSegmented={isSegmented}
          segmentedQuestionTitle={segmentedQuestionTitle}
          showPercent={shouldShowPercent}
          bucketsLayout={bucketsLayout}
          numColumns={numColumns}
          numBuckets={numBuckets}
          dotRadius={dotRadius}
          segments={segments}
          correctColor={correctColor}
          incorrectColor={incorrectColor}
          chartWidth={width}
          chartHeight={height}
        />
      )}
    </FullSizeContainer>
  );
};

export default Dots;
